import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { userAPI } from '../services/api'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'admin' | 'developer'
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, updateUser, isAuthenticated } = useAuthStore()
  const [isChecking, setIsChecking] = useState(true)
  const [shouldRedirect, setShouldRedirect] = useState(false)

  useEffect(() => {
    const verifyRole = async () => {
      if (!isAuthenticated) {
        setShouldRedirect(true)
        setIsChecking(false)
        return
      }

      // If no specific role is required, just check authentication
      if (!requiredRole) {
        setIsChecking(false)
        return
      }

      // If user already has the required role, no need to check server
      if (user?.role === requiredRole) {
        setIsChecking(false)
        return
      }

      // User doesn't have the required role in cache, check with server
      try {
        console.log('Verifying role with server...')
        const { data } = await userAPI.getProfile()
        console.log('Server returned user:', data)
        
        // Update the auth store with fresh data
        updateUser(data)
        
        // Check if user now has the required role
        if (data.role === requiredRole) {
          console.log('✅ User has required role:', requiredRole)
          setIsChecking(false)
          setShouldRedirect(false)
        } else {
          console.log('❌ User does not have required role. Has:', data.role, 'Needs:', requiredRole)
          setShouldRedirect(true)
          setIsChecking(false)
        }
      } catch (error) {
        console.error('Failed to verify role:', error)
        setShouldRedirect(true)
        setIsChecking(false)
      }
    }

    verifyRole()
  }, [user?.role, requiredRole, isAuthenticated, updateUser])

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-primary)' }}></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Verifying access...</p>
        </div>
      </div>
    )
  }

  if (shouldRedirect) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}










